import React, { createContext, useContext, useEffect, useState } from 'react';
import { isPrerendering, fallbackSettings } from '../prerenderData';
import { apiFetch } from '../services/api';

// SettingsContext — single source of truth for the storefront settings
// fetched from `/api/storefront/settings`. CQ-22: previously each page
// (Home, Quote, Contact, Inspection, About, Products, Footer) fetched
// independently in its own useEffect, generating ~7 identical network
// requests per pageview. Now fetched once at app bootstrap and shared
// via context.
//
// The first render seeds from the build-time snapshot in prerenderData, on
// the client as well as while pre-rendering: it is the data the pre-rendered
// HTML was made from, so React can adopt that DOM at hydration instead of
// failing (#418) and rebuilding the page. The live settings replace it as
// soon as the fetch resolves. `loading` therefore starts false.
const SettingsContext = createContext({ settings: {}, loading: true });

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(fallbackSettings);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isPrerendering) return;
    let cancelled = false;
    apiFetch('/api/storefront/settings')
      .then((r) => r.json())
      .then((data) => { if (!cancelled) setSettings(data || {}); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <SettingsContext.Provider value={{ settings, loading }}>
      {children}
    </SettingsContext.Provider>
  );
}

// Read just the settings map. Returns `{}` until the fetch resolves.
export function useSettings() {
  return useContext(SettingsContext).settings;
}

// Variant for callers that need to know whether the bootstrap fetch
// is still in flight (e.g. to render a skeleton).
export function useSettingsState() {
  return useContext(SettingsContext);
}
